import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { PageHeader } from "@/components/common/page-header"
import { RecordColumn, RecordSection } from "@/components/common/record"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { displayDate, isoDate } from "@/features/firms/lib/firm-fields"
import { useRecordForm } from "@/hooks/use-record-form"
import { firmQueries, useSetFirmAppointment } from "@/queries/firms"
import type { Firm, FirmKind } from "@/types/firm"

const formId = "firm-appointment"

const kindLabels: Record<FirmKind, string> = {
  full_ar: "Appointed representative",
  iar: "Introducer appointed representative",
}

export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/firms/$firmId/authorisation/appointment/"
)({
  component: FirmAppointmentPage,
})

function valuesFrom(firm: Firm) {
  return {
    kind: firm.kind as string,
    appointedOn: displayDate(firm.appointedOn),
  }
}

/**
 * What the firm is appointed to do, and from when.
 *
 * Its own page because the date is what the thirty-day notification is counted
 * back from: moving it moves a statutory deadline, and that does not belong on
 * the same form as a telephone number.
 */
function FirmAppointmentPage() {
  const { firmId } = Route.useParams()
  const navigate = useNavigate()
  const { data: firm } = useSuspenseQuery(firmQueries.detail(firmId))

  const form = useRecordForm({
    initial: valuesFrom(firm),
    mutation: useSetFirmAppointment(firmId),
    toValues: valuesFrom,
    toInput: (values) => ({
      kind: values.kind,
      appointedOn: isoDate(values.appointedOn),
    }),
    validate: (values) =>
      values.appointedOn !== "" && isoDate(values.appointedOn) === undefined
        ? { appointedOn: "Give the date as dd/mm/yyyy." }
        : {},
  })

  return (
    <>
      <PageHeader
        title="Appointment"
        actions={
          <Button
            type="submit"
            form={formId}
            disabled={!form.dirty || form.saving}
          >
            {form.saving ? <Spinner /> : null}
            Save
          </Button>
        }
      />

      <RecordColumn>
        <form
          id={formId}
          noValidate
          className="flex flex-col gap-10"
          onSubmit={async (event) => {
            await form.submit(event)

            if (!form.dirty) {
              return
            }

            await navigate({
              from: "/$organisation",
              to: "/$organisation/firms/$firmId/authorisation",
              params: { firmId },
            })
          }}
        >
          <RecordSection
            title="Appointment"
            description="What this firm is appointed to do, and the date it takes effect."
          >
            <Field>
              <FieldLabel htmlFor="firm-kind">Kind of appointment</FieldLabel>
              <Select
                value={form.values.kind}
                items={kindLabels}
                onValueChange={(kind) => form.setValue("kind", String(kind))}
              >
                <SelectTrigger id="firm-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(kindLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel htmlFor="firm-appointed-on">
                Appointment takes effect
              </FieldLabel>
              <Input
                id="firm-appointed-on"
                inputMode="numeric"
                placeholder="dd/mm/yyyy"
                value={form.values.appointedOn}
                aria-invalid={Boolean(form.errorsFor("appointedOn"))}
                onChange={(event) =>
                  form.setValue("appointedOn", event.target.value)
                }
              />
              <FieldDescription>
                The FCA must be told at least 30 days before this date. Changing
                it moves that deadline with it.
              </FieldDescription>
              <FieldError errors={form.errorsFor("appointedOn")} />
            </Field>
          </RecordSection>
        </form>
      </RecordColumn>
    </>
  )
}
